/**
 * Which channel an intake form was actually opened/submitted from.
 *
 * Captured at form-open time (the entry-point URL the client/advisor actually
 * used carries a `source` tag), not at invite time — so it reflects how the form
 * was really completed even when an advisor invites via one channel and the form
 * is opened via another.
 *
 * `email` / `link` / `fill-form` mirror the invite-client `TabType` channels.
 * `unknown` is the sentinel used when a form is opened without a `source` tag
 * (e.g. links issued before this was introduced) — these are logged, not thrown.
 */
export enum SurveySubmissionSource {
  EMAIL = 'email',
  LINK = 'link',
  FILL_FORM = 'fill-form',
  UNKNOWN = 'unknown',
}

export const SURVEY_SUBMISSION_SOURCES: readonly string[] = Object.values(SurveySubmissionSource);

export function isSurveySubmissionSource(value: unknown): value is SurveySubmissionSource {
  return typeof value === 'string' && SURVEY_SUBMISSION_SOURCES.includes(value);
}

/**
 * Normalizes a raw `source` value into a SurveySubmissionSource.
 * - missing/empty  → `UNKNOWN` (caller should log; a link was opened without a tag)
 * - a known value  → that value
 * - present but unrecognized → throws (programmer error / tampering), via `onInvalid`
 *
 * `onInvalid` lets each runtime supply its own error type (e.g. ApiExError on the server).
 */
export function parseSubmissionSource(
  raw: string | null | undefined,
  onInvalid: (value: string) => never,
): SurveySubmissionSource {
  if (raw === null || raw === undefined || raw === '') {
    return SurveySubmissionSource.UNKNOWN;
  }
  if (isSurveySubmissionSource(raw)) {
    return raw;
  }
  return onInvalid(raw);
}
